import React from 'react';
import { AlertTriangle, Info, AlertCircle } from 'lucide-react';
import AppModal from './AppModal';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  tone?: 'danger' | 'warning' | 'info';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  tone = 'danger',
}) => {
  const getToneStyles = () => {
    switch (tone) {
      case 'danger':
        return {
          icon: AlertTriangle,
          color: 'text-rose-600 dark:text-rose-400',
          bg: 'bg-rose-50 dark:bg-rose-950/30',
        };
      case 'warning':
        return {
          icon: AlertCircle,
          color: 'text-amber-600 dark:text-amber-400',
          bg: 'bg-amber-50 dark:bg-amber-950/30',
        };
      case 'info':
        return {
          icon: Info,
          color: 'text-blue-600 dark:text-blue-400',
          bg: 'bg-blue-50 dark:bg-blue-950/30',
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-brand-600 dark:text-brand-400',
          bg: 'bg-brand-50 dark:bg-brand-950/30',
        };
    }
  };

  const { icon: Icon, color, bg } = getToneStyles();

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      maxWidth="max-w-md"
      actions={[
        {
          label: cancelLabel,
          onClick: onCancel,
          variant: 'secondary',
        },
        {
          label: confirmLabel,
          onClick: onConfirm,
          variant: 'primary',
          loading: isLoading,
        },
      ]}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${bg}`}
        >
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mt-1">
            {message}
          </p>
        </div>
      </div>
    </AppModal>
  );
};

export default ConfirmationModal;
